/**
 * Events Design Tokens — flat token objects matching the FSC shape.
 *
 * All Events-page components import colours / styles from here instead
 * of calling `useTheme()` for colours.  To switch to the FSC palette
 * later, swap the values in this single file (or add a factory).
 *
 * Token shape mirrors:  app-fraud-score-checker/src/theme/index.ts
 */

import { createTheme } from "@mui/material/styles";
import { font, operational, radius } from "@mideye/design-tokens";

const eventsTokens = operational.events;

// ── Risk-level colours ───────────────────────────────────────────────
export const riskColors = eventsTokens.risk;

export type RiskLevel = keyof typeof riskColors;

export function getRiskLevel(score: number): RiskLevel {
  if (score >= 60) return "high";
  if (score >= 40) return "medium";
  return "low";
}

// ── Base colour palette ──────────────────────────────────────────────
export const colors = eventsTokens.colors;

// ── Chart colours — professional data-visualisation palette ──────────
export const chartColors = {
  primary: eventsTokens.chart.primary,
  secondary: colors.accent,
  tertiary: eventsTokens.chart.tertiary,
  quaternary: eventsTokens.chart.quaternary,
  quinary: eventsTokens.chart.quinary,
};

// Event type colours (matching FSC ActivityHistoryChart)
export const eventTypeColors: Record<string, string> = {
  successful: eventsTokens.eventType.successful,
  blocked: eventsTokens.eventType.blocked,
  user_not_found: eventsTokens.eventType.userNotFound,
  invalid_password: eventsTokens.eventType.invalidPassword,
  failed_second_factor: eventsTokens.eventType.failedSecondFactor,
};

// Attack type colours (matching FSC attackTypeColors)
export const attackTypeColors: Record<string, string> = {
  ENUMERATION: eventsTokens.attackType.enumeration,
  PASSWORD_SPRAY: eventsTokens.attackType.passwordSpray,
  CREDENTIAL_STUFFING: eventsTokens.attackType.credentialStuffing,
  BRUTE_FORCE: eventsTokens.attackType.bruteForce,
  UNKNOWN: eventsTokens.attackType.unknown,
  "2FA_BYPASS": eventsTokens.attackType.twoFactorBypass,
  MIXED: eventsTokens.attackType.mixed,
};

// ── Card styles ──────────────────────────────────────────────────────
export const cardStyles = {
  default: {
    backgroundColor: colors.background,
    borderRadius: radius.xl,
    border: `1px solid ${colors.border}`,
    boxShadow: eventsTokens.shadow.card,
    transition: "all 0.2s ease",
  },
  hover: {
    boxShadow: eventsTokens.shadow.cardHover,
  },
};

// ── Typography helpers ───────────────────────────────────────────────
export const typography = {
  sectionTitle: {
    fontSize: "0.75rem",
    fontWeight: 600,
    color: colors.textMuted,
    textTransform: "uppercase" as const,
    letterSpacing: "0.05em",
    marginBottom: "16px",
  },
  cardTitle: {
    fontSize: "1rem",
    fontWeight: 600,
    color: colors.textPrimary,
    lineHeight: 1.4,
  },
  cardDescription: {
    fontSize: "0.875rem",
    color: colors.textSecondary,
    marginTop: "4px",
  },
};

// ── MUI sx presets ───────────────────────────────────────────────────
export const tableHeadCellSx = {
  fontWeight: 600,
  fontSize: "0.8125rem",
  color: colors.textSecondary,
  borderColor: colors.border,
  backgroundColor: colors.backgroundSecondary,
  padding: "12px 16px",
} as const;

export const tableBodyCellSx = {
  fontSize: "0.875rem",
  color: colors.textPrimary,
  borderColor: colors.borderLight,
  padding: "12px 16px",
} as const;

// ── Accordion compact styles ─────────────────────────────────────────
export const accordionStyles = {
  backgroundColor: "transparent",
  boxShadow: "none",
  "&:before": { display: "none" },
  "&.Mui-expanded": { margin: 0 },
} as const;

export const accordionSummaryStyles = {
  minHeight: "40px",
  padding: "0 8px",
  "&.Mui-expanded": { minHeight: "40px" },
  "& .MuiAccordionSummary-content": {
    margin: "8px 0",
    "&.Mui-expanded": { margin: "8px 0" },
  },
} as const;

export const accordionDetailsStyles = {
  padding: "8px 8px 16px 8px",
} as const;

// ── Toggle button group style ────────────────────────────────────────
export const toggleButtonGroupStyle = {
  height: "32px",
  "& .MuiToggleButton-root": {
    px: 1.5,
    py: 0.5,
    fontSize: "0.75rem",
    fontWeight: 500,
    border: `1px solid ${colors.border}`,
    color: colors.textSecondary,
    "&.Mui-selected": {
      backgroundColor: colors.backgroundMuted,
      borderColor: colors.textPrimary,
      "&:hover": {
        backgroundColor: colors.backgroundSecondary,
      },
    },
    "&:hover": {
      backgroundColor: colors.backgroundSecondary,
    },
  },
} as const;

// ── Score slider gradient ────────────────────────────────────────────
export const scoreSliderSx = {
  color: colors.primary,
  "& .MuiSlider-thumb": {
    backgroundColor: colors.background,
    border: `2px solid ${colors.primary}`,
    width: 16,
    height: 16,
  },
  "& .MuiSlider-track": {
    background: `linear-gradient(90deg, ${riskColors.low.main} 0%, ${riskColors.medium.main} 50%, ${riskColors.high.main} 100%)`,
    border: "none",
  },
  "& .MuiSlider-rail": {
    opacity: 0.3,
    backgroundColor: colors.border,
  },
  "& .MuiSlider-markLabel": {
    color: colors.textMuted,
    fontSize: "0.7rem",
  },
} as const;

// ══════════════════════════════════════════════════════════════════════
// Scoped light MUI theme for the Events page.
// This resets the Aegis dark overrides so our colour tokens control
// every surface, text, border, and component appearance.
// ══════════════════════════════════════════════════════════════════════
export const eventsLightTheme = createTheme({
  palette: {
    mode: "light",
    primary: { main: colors.primary, contrastText: colors.primaryForeground },
    secondary: { main: colors.accent },
    error: { main: riskColors.high.main },
    warning: { main: riskColors.medium.main },
    success: { main: riskColors.low.main },
    background: {
      default: colors.backgroundSecondary,
      paper: colors.background,
    },
    text: {
      primary: colors.textPrimary,
      secondary: colors.textSecondary,
    },
    divider: colors.border,
  },
  typography: {
    fontFamily: font.family.sans,
    button: { textTransform: "none" as const, fontWeight: 600 },
  },
  shape: { borderRadius: 8 },
  components: {
    MuiPaper: {
      styleOverrides: {
        root: { backgroundImage: "none", backgroundColor: colors.background, borderRadius: radius.xl },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: `1px solid ${colors.borderLight}`,
          padding: "12px 16px",
          fontSize: "0.875rem",
          color: colors.textPrimary,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { fontWeight: 600 },
        outlined: { borderColor: colors.border },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: { textTransform: "none", fontWeight: 600, borderRadius: radius.lg },
        outlined: {
          borderColor: colors.border,
          color: colors.textSecondary,
          "&:hover": {
            borderColor: colors.borderFocus,
            backgroundColor: colors.backgroundMuted,
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-root": {
            backgroundColor: colors.background,
            borderRadius: radius.lg,
            "& fieldset": { borderColor: colors.border },
            "&:hover fieldset": { borderColor: colors.borderFocus },
            "&.Mui-focused fieldset": { borderColor: colors.primary },
          },
          "& .MuiInputLabel-root": { color: colors.textMuted },
        },
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: { backgroundColor: "transparent", boxShadow: "none", "&:before": { display: "none" } },
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          color: colors.textSecondary,
          borderColor: colors.border,
        },
      },
    },
    MuiSlider: {
      styleOverrides: {
        root: { color: colors.primary },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: { color: colors.textMuted },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        paper: { backgroundColor: colors.background, border: `1px solid ${colors.border}` },
      },
    },
    MuiPopover: {
      styleOverrides: {
        paper: { backgroundColor: colors.background, border: `1px solid ${colors.border}` },
      },
    },
  },
});
